import React from 'react';
import { Dialog } from '@progress/kendo-react-dialogs';

import { useFullscreen } from '../hooks/use-fullscreen';

type CameraModalProps = {
  isOpen: boolean;
  mode?: 'photo' | 'video';
  isResumeFullscreenAfterCamera?: boolean;
  onClose: () => void;
  onCapture: (imageData: string) => void;
  onVideoCapture?: (blob: Blob, url: string) => void;
};

const Button = ({
  children,
  additionalClass,
  onClick
}: {
  children: React.ReactNode;
  additionalClass: string;
  onClick: () => void;
}) => {
  return (
    <div
      className={`d-flex align-items-center cursor-pointer justify-content-center rounded-circle p-2 border border-black ${additionalClass}`}
      style={{ width: '50px', height: '50px' }}
      onClick={onClick}>
      {children}
    </div>
  );
};

const CameraModal = ({
  isOpen,
  mode = 'photo',
  isResumeFullscreenAfterCamera,
  onClose,
  onCapture,
  onVideoCapture
}: CameraModalProps) => {
  const { enterFullscreen, exitFullscreen } = useFullscreen();

  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const streamRef = React.useRef<MediaStream | null>(null);
  const mediaRecorderRef = React.useRef<MediaRecorder | null>(null);
  const recordedChunksRef = React.useRef<Blob[]>([]);

  const [capturing, setCapturing] = React.useState(false);
  const [hasRecordedVideo, setHasRecordedVideo] = React.useState(false);
  const [facingMode, setFacingMode] = React.useState<'user' | 'environment'>(
    'environment'
  );

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 },
          frameRate: { ideal: 30, max: 30 }
        },
        audio:
          mode === 'video'
            ? {
                echoCancellation: true,
                noiseSuppression: true
              }
            : false
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }

      isResumeFullscreenAfterCamera ? enterFullscreen() : exitFullscreen();
    } catch (err) {
      console.error('Camera error', err);
    }
  };

  const stopCamera = () => {
    try {
      mediaRecorderRef.current?.stop();
    } catch {}

    streamRef.current?.getTracks().forEach((track) => track.stop());

    streamRef.current = null;
    mediaRecorderRef.current = null;
    recordedChunksRef.current = [];

    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.srcObject = null;
    }

    setCapturing(false);
    setHasRecordedVideo(false);
  };

  const handleSwitchCamera = () => {
    stopCamera();
    setFacingMode((prev) => (prev === 'user' ? 'environment' : 'user'));
  };

  //#region Photo capture
  const handlePhotoCapture = () => {
    const video = videoRef.current;
    if (!video) return;

    const canvas = document.createElement('canvas');

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(video, 0, 0);

    const image = canvas.toDataURL('image/jpeg');
    onCapture(image);
  };
  //#region End photo capture

  //#region Video record
  const handleDataAvailable = (event: BlobEvent) => {
    if (event.data.size > 0) recordedChunksRef.current.push(event.data);
  };

  const handleStartCaptureClick = () => {
    const stream = streamRef.current;
    if (!stream) return;

    recordedChunksRef.current = [];

    const mimeType = MediaRecorder.isTypeSupported('video/mp4')
      ? 'video/mp4'
      : 'video/webm';

    const recorder = new MediaRecorder(stream, {
      mimeType,
      videoBitsPerSecond: 4000000
    });

    mediaRecorderRef.current = recorder;

    recorder.addEventListener('dataavailable', handleDataAvailable);
    recorder.start(1000);

    setCapturing(true);
    setHasRecordedVideo(false);
  };

  const handleStopCaptureClick = () => {
    mediaRecorderRef.current?.stop();
    setCapturing(false);
    setHasRecordedVideo(true);
  };

  const handleSubmitVideo = () => {
    const mimeType = MediaRecorder.isTypeSupported('video/mp4')
      ? 'video/mp4'
      : 'video/webm';

    const blob = new Blob(recordedChunksRef.current, { type: mimeType });

    const url = URL.createObjectURL(blob);

    onVideoCapture?.(blob, url);

    recordedChunksRef.current = [];
    setHasRecordedVideo(false);
  };

  const handleCancelVideo = () => {
    recordedChunksRef.current = [];
    setHasRecordedVideo(false);
  };
  //#region End video record

  const handleClose = () => {
    stopCamera();
    onClose();
  };

  React.useEffect(() => {
    if (isOpen) startCamera();

    return () => {
      stopCamera();
    };
  }, [isOpen, facingMode]);

  if (!isOpen) return null;

  return (
    <Dialog title="" onClose={handleClose} className="webcam-modal">
      <div className="position-relative h-100">
        <div
          className="position-absolute top-0 end-0 p-3 d-flex gap-3"
          style={{
            zIndex: 999
          }}>
          <Button additionalClass="bg-white" onClick={handleSwitchCamera}>
            <span className="material-symbols-outlined">cameraswitch</span>
          </Button>

          <Button additionalClass="bg-white" onClick={handleClose}>
            <span className="material-symbols-outlined">close</span>
          </Button>
        </div>

        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
        />

        <div
          className="position-absolute bottom-0 start-50 translate-middle-x p-4"
          style={{
            zIndex: 999
          }}>
          {mode === 'photo' ? (
            <Button additionalClass="bg-white" onClick={handlePhotoCapture}>
              <span className="material-symbols-outlined">photo_camera</span>
            </Button>
          ) : capturing ? (
            <Button
              additionalClass="bg-danger"
              onClick={handleStopCaptureClick}>
              <span className="material-symbols-outlined">stop_circle</span>
            </Button>
          ) : hasRecordedVideo ? (
            <div className="d-flex gap-3">
              <Button additionalClass="bg-danger" onClick={handleCancelVideo}>
                <span className="material-symbols-outlined">close</span>
              </Button>

              <Button additionalClass="bg-success" onClick={handleSubmitVideo}>
                <span className="material-symbols-outlined">check</span>
              </Button>
            </div>
          ) : (
            <Button
              additionalClass="bg-white"
              onClick={handleStartCaptureClick}>
              <span className="material-symbols-outlined">videocam</span>
            </Button>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default CameraModal;
